<template>
  <RevDialog
    :name
    :title="
      i18n(translations.title, {
        mobileService: mobileServiceName,
      })
    "
    @open="fetchRedirectionUrls"
  >
    <template #body>
      <div class="space-y-32">
        <RevInfoBlock
          :icon="IconInfo"
          :title="
            i18n(translations.text1, {
              mobileService: mobileServiceName,
            })
          "
          variant="info"
        />

        <p>
          {{ i18n(translations.text2) }}
        </p>

        <RevSpinner
          v-if="isLoading"
          :alternative-text="i18n(translations.loading)"
          class="mx-auto"
          data-test="mobile-plan-redirection-urls-loader"
        />

        <RevInfoBlock
          v-else-if="redirectionUrlsError"
          :content="i18n(translations.errorContent)"
          :cta="i18n(translations.errorRetryCta)"
          :icon="IconWarning"
          :title="i18n(translations.errorTitle)"
          data-test="mobile-plan-redirection-urls-error"
          variant="danger"
          @click="fetchRedirectionUrls"
        />

        <div v-else class="space-y-12">
          <RevButton
            v-if="portalUrl"
            full-width="always"
            :to="portalLink"
            data-test="mobile-plan-portal-cta"
            variant="primary"
          >
            {{
              i18n(translations.cta, {
                mobileService: mobileServiceName,
              })
            }}
          </RevButton>

          <RevButton
            v-if="cancellationUrl"
            full-width="always"
            :to="cancellationLink"
            data-test="mobile-plan-cancellation-cta"
            variant="secondary"
          >
            {{ i18n(translations.cancellationCta) }}
          </RevButton>
        </div>
      </div>
    </template>
  </RevDialog>
</template>

<script lang="ts" setup>
import { useAsyncData } from '#imports'
import { computed } from 'vue'

import type { LinkExternal } from '@backmarket/http-api/src/api-models/LinkExternal'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevSpinner } from '@ds/components/Spinner'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconWarning } from '@ds/icons/IconWarning'

import { CARE_COMMON_MODALS_NAMES } from '../names'

import translations from './MobileServiceRedirectModal.translations'
import type { MobileServiceRedirectModalProps } from './MobileServiceRedirectModal.types'

const props = defineProps<MobileServiceRedirectModalProps>()

const i18n = useI18n()
const $httpFetch = useHttpFetch()

const name = `${CARE_COMMON_MODALS_NAMES.mobileServiceRedirectModal}-${props.orderlineId}`

const redirectionUrlsKey = getAsyncDataKey('mobile-plan-redirection-urls', {
  subscriptionId: props.subscriptionId,
})

const {
  data,
  pending: isLoading,
  error: redirectionUrlsError,
  execute,
} = useAsyncData(
  redirectionUrlsKey,
  () =>
    $httpFetch(
      '/mobile-plan/api/v1/subscriptions/:subscriptionId/redirection-urls',
      {
        pathParams: {
          subscriptionId: props.subscriptionId,
        },
      },
    ),
  { immediate: false },
)

const portalUrl = computed(() => data.value?.portalUrl ?? null)
const cancellationUrl = computed(() => data.value?.cancellationUrl ?? null)

const portalLink = computed<LinkExternal | null>(() =>
  portalUrl.value ? { href: portalUrl.value, type: 'external' } : null,
)

const cancellationLink = computed<LinkExternal | null>(() =>
  cancellationUrl.value
    ? { href: cancellationUrl.value, type: 'external' }
    : null,
)

async function fetchRedirectionUrls() {
  await execute()
}
</script>
